import axios from "axios";
import { clearCookies, getCookies, setCookie } from "../helpers/utils";
import { COOKIES } from "../constants";
import { getNewToken } from "./authentication.service";
import { IAuthTokens, IRequestError } from "../interfaces/authentication.interface";
export type RequestError = { message: string; code?: string };

const DO_NOT_RETRY_ENDPOINTS = ["login", "auth/refresh-token", "change-password"];

let isRefreshing = false;
let failedQueue: { resolve: (token: string | null) => void; reject: (error: any) => void }[] = [];

export const request = axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getCookies()[COOKIES.TOKEN]}`
    }
});

export const basicRequest = axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${getEncodedCredentials()}`
    }
});

export const strapiRequest = axios.create({
    baseURL: process.env.REACT_APP_BASE_CONTENT_API_URL,
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`
    }
});

export const vimeoRequest = axios.create({
    baseURL: "https://api.vimeo.com",
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_VIMEO_API_TOKEN}`
    }
});

function getEncodedCredentials(): string {
    const clientName = process.env.REACT_APP_BASIC_AUTH_CLIENT_NAME;
    const clientSecret = process.env.REACT_APP_BASIC_AUTH_CLIENT_SECRET;
    return btoa(`${clientName}:${clientSecret}`);
}

const getError = (err: any) => {
    if (!(err?.status && err?.code && err?.message)) {
        return err;
    }

    const isAxiosError = axios.isAxiosError(err);

    if (isAxiosError && err.response) {
        return {
            status: err.response.status,
            code: err.response.data.code,
            message: err.response.data.message
        };
    } else {
        return {
            status: 400,
            code: 0,
            message:
                "Something went wrong. Please check your internet connection or contact our support."
        };
    }
};

request.interceptors.request.use(
    function (config) {
        config.headers["Authorization"] = `Bearer ${getCookies()[COOKIES.TOKEN]}`;
        return config;
    },
    function (err) {
        return Promise.reject(err);
    }
);

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

request.interceptors.response.use((response) => response, retryRequest);

async function retryRequest(err: any) {
    const originalRequest = err.config;

    if (
        err.response?.status !== 401 ||
        err.status !== 401 ||
        originalRequest._retry ||
        DO_NOT_RETRY_ENDPOINTS.some((k) => originalRequest.url.match(k))
    ) {
        // NOT 401 STATUS ERRORS 
        // NOT RETRY ENDPOINTS
        return Promise.reject(getError(err));
    }

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({
                resolve: (token: string | null) => {
                    originalRequest.headers["Authorization"] = `Bearer ${token}`;
                    resolve(request(originalRequest));
                },
                reject: (err: any) => {
                    reject(err);
                }
            });
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
        const response = await getNewToken(getCookies()[COOKIES.REFRESH_TOKEN]);

        if (response.success) {
            const tokens = response.data as IAuthTokens;

            setCookie(COOKIES.TOKEN, tokens.access_token, null, "/", null, true);
            setCookie(COOKIES.REFRESH_TOKEN, tokens.refresh_token, null, "/", null, true);

            request.defaults.headers.common["Authorization"] = `Bearer ${tokens.access_token}`;

            (window as any).TokenManager?.postMessage(`
                {
                    "access_token": "${tokens.access_token}",
                    "refresh_token": "${tokens.refresh_token}"
                }
            `);

            processQueue(null, tokens.access_token);

            return request(originalRequest);
        } else {
            throw new Error((response.data as IRequestError).code);
        }
    } catch (err) {
        processQueue(err, null);
        clearCookies();
        return Promise.reject(getError(err));
    } finally {
        isRefreshing = false;
    }
}
